import { Stack } from '@mui/material';
import { StaticDatePickerProps } from '@mui/x-date-pickers/StaticDatePicker';
import dayjs from 'dayjs';
import { Controller, FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ClockAddIcon } from '@/shared/assets/icons/clock-add-icon';
import { Alert } from '@/shared/components/ui/alert/alert';
import { IconButton } from '@/shared/components/ui/button/icon-button/icon-button';
import { FormDateTimePickerContainer } from '@/shared/components/ui/form/components/form-date-time-picker/form-date-time-picker.styled';
import { TFormMonthYearPickerProps } from '@/shared/components/ui/form/components/form-date-time-picker/form-date-time-picker.types';
import { FormLabel } from '@/shared/components/ui/form/components/form-label/form-label';
import { useValidationMessage } from '@/shared/components/ui/form/hooks/use-validation-message/use-validation-message';
import { MonthYearPickerPopover } from '@/shared/components/ui/month-year-select/month-year-picker-popover';

export const FormMonthYearPicker = <TFieldValues extends FieldValues>({
  name,
  label,
  defaultValue,
  rules,
  shouldUnregister,
  hint,
  triggerIcon,
  triggerLabel,
  dateTimePickerProps,
  futureOnly = false,
  pastOnly = false,
  maxMonths,
  triggerElement,
  required,
  disabled,
  onAccept,
  ...rest
}: TFormMonthYearPickerProps<TFieldValues>) => {
  const { t } = useTranslation();
  const { control, trigger } = useFormContext<TFieldValues>();
  const resolveValidationMessage = useValidationMessage();

  // Month values are stored/compared in UTC so the selected month is stable
  // regardless of the viewer's timezone (see handleDateChange).
  const getDatePickerProps = (): Partial<StaticDatePickerProps> => {
    const props: Partial<StaticDatePickerProps> = { ...dateTimePickerProps };
    if (futureOnly)
      props.minDate = dayjs.utc().add(1, 'month').startOf('month');
    if (pastOnly) props.maxDate = dayjs.utc().startOf('month');
    if (maxMonths && maxMonths > 0) {
      props.maxDate = dayjs.utc().add(maxMonths, 'month').endOf('month');
    }
    return props;
  };

  const handleDateChange = (
    value: dayjs.Dayjs | null,
    onChange: (value: string | null) => void,
  ) => {
    if (value) {
      const dateValue = dayjs
        .utc()
        .year(value.year())
        .month(value.month())
        .startOf('month');
      onChange(dateValue.toISOString());
    } else {
      onChange(null);
    }
    void trigger(name);
  };

  const renderTrigger = (value: string | null) => {
    if (triggerElement) return triggerElement(value);

    const className = value ? 'active-icon-button' : undefined;

    if (triggerLabel) {
      return (
        <IconButton className={className} disabled={disabled}>
          {triggerIcon || <ClockAddIcon />}
          {triggerLabel(value)}
        </IconButton>
      );
    }

    return (
      <IconButton
        aria-label={label || t('ui:select_date')}
        className={className}
        tooltip={label}
        disabled={disabled}
      >
        {triggerIcon || <ClockAddIcon />}
      </IconButton>
    );
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      disabled={disabled}
      shouldUnregister={shouldUnregister}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <FormDateTimePickerContainer gap="0.75rem" width="100%">
          <Stack
            direction={triggerElement ? 'column' : 'row'}
            gap="0.5rem"
            alignItems="center"
          >
            <Stack width={triggerElement ? '100%' : 'auto'}>
              {triggerElement && label && (
                <FormLabel label={label} required={required} />
              )}
              <MonthYearPickerPopover
                trigger={renderTrigger(value)}
                value={value ? dayjs.utc(value) : null}
                onChange={(newValue) => {
                  handleDateChange(newValue, onChange);
                  onAccept?.();
                }}
                onAccept={onAccept}
                disabled={disabled}
                datePickerProps={getDatePickerProps()}
                {...rest}
              />
            </Stack>
            {error && (
              <Alert
                color="red"
                text={
                  resolveValidationMessage(error.message) ||
                  t('ui:invalid_date')
                }
              />
            )}
          </Stack>
          {hint && <Alert color="blue" text={hint} />}
        </FormDateTimePickerContainer>
      )}
    />
  );
};
